import { Permission } from "./../../../../../../utils/enum/permission.enum";
import { Component, inject, Input, OnInit, signal } from "@angular/core";
import { Router } from "@angular/router";
import { LayoutType } from "../../../core/configs/config";
import { LayoutInitService } from "../../../core/layout-init.service";
import { LayoutService } from "../../../core/layout.service";
import { SYSTEM_NAVIGATION_MENU } from "../../../../../menu/menus.data";
import { CoreMenuItem } from "src/app/menu/core-menu";
import { NgxPermissionsService } from "ngx-permissions";
import { AuthService } from "src/app/pages/auth";
import { RoleID } from "src/utils/enum/role.enum";

@Component({
  selector: "app-header-menu",
  templateUrl: "./header-menu.component.html",
  styleUrls: ["./header-menu.component.scss"],
  standalone: false,
})
export class HeaderMenuComponent implements OnInit {
  constructor(
    private router: Router,
    private layout: LayoutService,
    private layoutInit: LayoutInitService,
    private permissionsService: NgxPermissionsService
  ) {}
  #auth = inject(AuthService);

  SYSTEM_NAVIGATION_MENU = SYSTEM_NAVIGATION_MENU;
  Permission = Permission;
  canAccess = signal<boolean>(true);
  // The SIGNAL, not a snapshot of it. Read once in the constructor the menu
  // never learned that anyone had signed in, so it stayed in its
  // logged-out shape until a full page reload.
  currentUser = this.#auth.currentUser;

  ngOnInit(): void {
    this.permissionsService
      .hasPermission(Permission.ENTER_COURSE)
      .then((res) => this.canAccess.update(() => res));
  }

  /**
   * Whether this user should be offered this entry.
   *
   * The old test was `currentUser() && canAccess()` for EVERY entry that
   * declared a role, so the `role: [...]` array in the menu data was never
   * actually read — one global course permission decided everything. A parent
   * fails that permission, so signing in as one hid the classroom entries and
   * offered nothing in their place: คลังคอร์ส, บันทึกข้อมูลการเรียน and
   * จัดการนักเรียน all existed and worked, with no link to any of them
   * anywhere in the app.
   *
   * Now the entry's own role decides. The course permission stays as an extra
   * gate on the STUDENT classroom, which is what it was there for.
   */
  canSee(menu: CoreMenuItem): boolean {
    if (!menu.role?.length) return true;

    const user = this.currentUser();
    if (!user) return false;

    const roleId = Number((user as any)?.user?.role?.id ?? (user as any)?.role?.id);
    if (!(menu.role as (number | string)[]).map(Number).includes(roleId)) return false;

    return roleId === RoleID.MEMBER ? this.canAccess() : true;
  }

  calculateMenuItemCssClass(url: string): string {
    return checkIsActive(this.router.url, url) ? "active" : "";
  }

  /**
   * Whether a top-level entry should read as the current one.
   *
   * `routerLinkActive` matches by prefix, so /online-class/quiz lit BOTH
   * ห้องเรียนออนไลน์ and แบบฝึกหัด at once. Plain `exact: true` is not the answer
   * either — it would stop ห้องเรียนออนไลน์ highlighting while the student is
   * inside a course at /online-class/:id/watch, which is exactly when it should.
   *
   * So: prefix matching as before, except a subtree another entry owns belongs
   * to that entry.
   */
  isActive(menu: CoreMenuItem): boolean {
    if (!menu.url) return false;
    const current = getCurrentUrl(this.router.url);
    if (current === menu.url) return true;
    if (!current.startsWith(menu.url + "/")) return false;

    return !this.SYSTEM_NAVIGATION_MENU.some(
      (other) =>
        other !== menu &&
        !!other.url &&
        other.url.startsWith(menu.url + "/") &&
        (current === other.url || current.startsWith(other.url + "/"))
    );
  }

  setBaseLayoutType(layoutType: LayoutType) {
    this.layoutInit.setBaseLayoutType(layoutType);
  }

  setToolbar(
    toolbarLayout: "classic" | "accounting" | "extended" | "reports" | "saas"
  ) {
    const currentConfig = { ...this.layout.layoutConfigSubject.value };
    if (currentConfig && currentConfig.app && currentConfig.app.toolbar) {
      currentConfig.app.toolbar.layout = toolbarLayout;
      this.layout.saveBaseConfig(currentConfig);
    }
  }
}

const getCurrentUrl = (pathname: string): string => {
  return pathname.split(/[?#]/)[0];
};

const checkIsActive = (pathname: string, url: string) => {
  const current = getCurrentUrl(pathname);
  if (!current || !url) {
    return false;
  }

  if (current === url) {
    return true;
  }

  if (current.indexOf(url) > -1) {
    return true;
  }

  return false;
};
